import _ from 'lodash';

import { GenericLazyString } from '@eb/i18n';
import LOCALE_OVERRIDES from '../constants/localeOverrides';

/**
 * Recursively merges the initial object with properties from another objects
 *
 * @param  {object} memo Passed in from reduce callback function
 * @param {object} override Object that contains properties to override
 * @return {object} A new object that has properties merged from the previous two objects
 */
const _getSettingsWithOverrides = (memo, override) => {
    if (!override) {
        return memo;
    }

    return _.mapValues(memo, (val, key) => {
        let finalValue = val;
        const newVal = override[key];

        if (newVal !== undefined) {
            finalValue =
                _.isObject(newVal) &&
                !_.isArray(newVal) &&
                !(val instanceof GenericLazyString)
                    ? _getSettingsWithOverrides(val, newVal)
                    : newVal;
        }

        return finalValue;
    });
};

/**
 * Merges initial settings with authentication and international settings
 *
 * @param  {string} locale user's locale ('en_US', 'fr_FR')
 * @param  {string} userPlan active assortment plan of current user
 * @param  {bool} isAuthenticated whether or not the user is logged in vs. logged out
 * @param  {object} defaultSettings base settings for the page
 * @param  {object} userSettings settings to override when user is logged in
 * @param  {string} page current page for fetching settings to override for a specific locale
 * @param  {string} currentExperiment name of experiment that should match key in `experimentSettings`
 * @param  {object} experimentSettings settings to override if an experiment is running
 * @param  {object} additionalSettings any additional settings to override the defaults
 * @return {object} newly transformed settings with all of the settings merged
 */

export default ({
    locale,
    userPlan,
    isAuthenticated,
    defaultSettings,
    page,
    userSettings = {},
    currentExperiment,
    experimentSettings = {},
    additionalSettings = null,
}) => {
    const overrides = {
        user: isAuthenticated && userSettings[userPlan],
        international:
            locale && LOCALE_OVERRIDES[locale]
                ? LOCALE_OVERRIDES[locale][page]
                : {},
        experiment: experimentSettings[currentExperiment] || {},
        additionalSettings,
    };

    return _.reduce(overrides, _getSettingsWithOverrides, defaultSettings);
};
